import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, FileSignature, CheckCircle2, Loader2, Mail, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import NavBar from '../components/NavBar';
import { PlanPicker } from '../components/onboarding/PlanPicker';
import { CountrySelect, PhoneInput } from '../components/onboarding/CountryPhoneFields';
import { vatRateFor } from '../components/onboarding/countries';
import { API } from '../services/http';
import { getLastCta } from '../services/ctaTracking';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { TerritoryMap } from '../components/landing/TerritoryMap';
import { TerritoryGoals } from '../components/landing/TerritoryGoals';

const TERRITOIRES = ['Guadeloupe', 'Martinique', 'Guyane', 'La Réunion', 'Mayotte', 'Saint-Martin'];
const inputCls = 'w-full h-11 rounded-xl px-3.5 text-sm text-white placeholder-white/35 bg-white/[0.05] border border-[#D9B35A]/25 focus:outline-none focus:ring-1 focus:ring-[#D9B35A]/60';
const labelCls = 'block text-xs text-white/60 mb-1.5';

const Stepper = ({ current }) => {
  const { t } = useTranslation();
  const steps = [t('vendorOnboarding.stepPayment'), t('vendorOnboarding.stepConvention'), t('vendorOnboarding.stepSignature'), t('vendorOnboarding.stepActivation')];
  return (
    <div className="flex items-center justify-center gap-2 mb-8" data-testid="vendor-onboarding-stepper">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
            i < current ? 'bg-[#7BC94E] text-[#12240a]' : i === current ? 'bg-[#D4AF37] text-[#1F0A33]' : 'bg-white/10 text-white/50'
          }`}>{i < current ? '✓' : i + 1}</span>
          <span className={`text-xs hidden sm:inline ${i === current ? 'text-[#E9CF8E] font-semibold' : 'text-white/45'}`}>{s}</span>
          {i < steps.length - 1 && <span className="w-6 h-px bg-white/15" />}
        </div>
      ))}
    </div>
  );
};

export default function VendorOnboardingPage() {
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [goalZone, setGoalZone] = useState('GUADELOUPE');
  const [busy, setBusy] = useState(false);
  const [, setOb] = useState(null);
  const buyerOnly = params.get('type') === 'acheteur_pro';
  const [start, setStart] = useState({ company: '', legal_form: '', first_name: '', last_name: '', email: '', siret: '', plan_slug: params.get('plan') || 'ess-acces-pro', member_type: buyerOnly ? 'buyer' : 'vendor', country: 'GP', cgu_accepted: false });
  const [dial, setDial] = useState('+590|GP');
  const [phoneNum, setPhoneNum] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [plans, setPlans] = useState([]);
  const [conv, setConv] = useState({ forme_sociale: '', capital: '', rcs_ville: '', adresse: '', rep_nom: '', rep_prenom: '', rep_qualite: '', territoires: [], lieu_signature: '' });
  const [sign, setSign] = useState({ nom: '', qualite: '', lu_approuve: false });

  useEffect(() => {
    fetch(`${API}/public/member-profiles`)
      .then((r) => r.json())
      .then((d) => setProfiles(Array.isArray(d.profiles) ? d.profiles : []))
      .catch(() => {});
  }, []);

  const sponsorCode = params.get('parrain') || localStorage.getItem('referral_code');
  useEffect(() => {
    const code = params.get('parrain');
    if (code) localStorage.setItem('referral_code', code.trim().toUpperCase());
    const promo = params.get('promo');
    if (promo) localStorage.setItem('pro_welcome_code', promo.trim().toUpperCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [welcomePromo, setWelcomePromo] = useState(null);
  useEffect(() => {
    const code = (params.get('promo') || localStorage.getItem('pro_welcome_code') || '').trim().toUpperCase();
    if (!code) return;
    fetch(`${API}/public/pro-welcome-code/${encodeURIComponent(code)}`)
      .then(async (r) => {
        if (r.ok) return r.json();
        const d = await r.json().catch(() => ({}));
        if (r.status === 410) toast.info('Votre code de bienvenue -20 % a expiré (validité 30 jours).');
        else if (r.status === 409) toast.info('Ce code de bienvenue a déjà été utilisé.');
        throw new Error(d.detail || 'Code invalide');
      })
      .then((d) => setWelcomePromo(d))
      .catch(() => { setWelcomePromo(null); localStorage.removeItem('pro_welcome_code'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const oid = params.get('onboarding_id');

  useEffect(() => {
    if (params.get('step') === 'paid' && oid) {
      fetch(`${API}/vendor-onboarding/${oid}/status`)
        .then((r) => r.json())
        .then((d) => {
          setOb(d);
          setConv((c) => ({ ...c, forme_sociale: c.forme_sociale || d.legal_form || '', rep_prenom: c.rep_prenom || d.first_name || '', rep_nom: c.rep_nom || d.last_name || '' }));
          if (d.status === 'PAID' || d.status === 'INFO_COMPLETED') { setStep(1); toast.success(t('vendorOnboarding.paidToast')); }
          else if (d.status === 'SIGNED' || d.status === 'ACTIVATED') setStep(3);
          else toast.info(t('vendorOnboarding.pendingToast'));
        });
    }
    if (params.get('step') === 'cancelled') toast.error(t('vendorOnboarding.cancelledToast'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const launchPayment = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/vendor-onboarding/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...start, referral_code: (sponsorCode || '').trim().toUpperCase(), promo_code: welcomePromo?.code || '', source_cta: getLastCta(), contact_name: `${start.first_name} ${start.last_name}`.trim(), phone: `${dial.split('|')[0]} ${phoneNum}`.trim(), origin_url: window.location.origin, locale: i18n.language?.startsWith('gcf') ? 'gcf' : (i18n.language || 'fr').slice(0, 2) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erreur');
      window.location.href = d.checkout_url;
    } catch (err) { toast.error(err.message); setBusy(false); }
  };

  const saveConvention = async (e) => {
    e.preventDefault();
    if (conv.territoires.length === 0) return toast.error(t('vendorOnboarding.territoireErr'));
    setBusy(true);
    try {
      const r = await fetch(`${API}/vendor-onboarding/${oid}/convention-fields`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(conv),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erreur');
      setStep(2);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const doSign = async () => {
    if (!sign.lu_approuve) return toast.error(t('vendorOnboarding.signCheckErr'));
    if (!sign.nom || !sign.qualite) return toast.error(t('vendorOnboarding.signFieldsErr'));
    setBusy(true);
    try {
      const r = await fetch(`${API}/vendor-onboarding/${oid}/sign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sign),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Erreur');
      toast.success(t('vendorOnboarding.signedToast', { code: d.verification_code }));
      setStep(3);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" data-testid="vendor-onboarding-page">
      <NavBar />
      <div className="max-w-3xl mx-auto px-4 pt-28 pb-16">
        <h1 className="text-3xl font-bold text-white text-center mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
          {t('vendorOnboarding.title')} <span className="text-[#D9B35A]">{buyerOnly ? t('vendorOnboarding.buyerTitle') : t('vendorOnboarding.titleAccent')}</span>
        </h1>
        <p className="text-white/60 text-sm text-center mb-8">{t('vendorOnboarding.subtitle')}</p>
        <Stepper current={step} />

        {step === 0 && (
          <form onSubmit={launchPayment} className="glass-panel rounded-[22px] p-6 space-y-4" data-testid="vendor-start-form">
            {welcomePromo && (
              <div className="rounded-xl border-2 border-dashed border-[#D9B35A]/60 bg-[#D9B35A]/10 px-4 py-3" data-testid="welcome-promo-banner">
                <p className="text-sm text-[#E9CF8E] font-bold m-0">
                  🎁 Offre de bienvenue : -{welcomePromo.percent} % sur votre première adhésion
                </p>
                <p className="text-[11px] text-white/55 m-0 mt-0.5">
                  Code <span className="font-mono font-bold text-[#E9CF8E]">{welcomePromo.code}</span> appliqué automatiquement au paiement.
                  {welcomePromo.expires_at && <span className="text-orange-300/90"> Valable jusqu'au {new Date(welcomePromo.expires_at).toLocaleDateString('fr-FR')}.</span>}
                </p>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 pb-1 border-b border-white/[0.08]"
              data-testid="onboarding-language-picker">
              <span className="text-xs text-white/60">
                {t('vendorOnboarding.chooseLanguage', 'Choisissez votre langue · Choose your language · Elija su idioma · Chwazi lang a\'w')}
              </span>
              <LanguageSwitcher />
            </div>
            <div>
              <p className="text-xs text-white/60 mb-2">{t('vendorOnboarding.memberTypeLabel')}</p>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="member-type-choice">
                {(profiles.length ? profiles : [
                  { slug: 'vendor', titles: { fr: t('vendorOnboarding.vendorTitle') }, descriptions: { fr: t('vendorOnboarding.vendorDesc') } },
                  { slug: 'buyer', titles: { fr: t('vendorOnboarding.buyerTitle') }, descriptions: { fr: t('vendorOnboarding.buyerDesc') } },
                ]).filter((tp) => !buyerOnly || tp.slug === 'buyer').map((tp) => {
                  const lang = i18n.language?.startsWith('gcf') ? 'gcf' : (i18n.language || 'fr').slice(0, 2);
                  return (
                    <button type="button" key={tp.slug} data-testid={`member-type-${tp.slug}`}
                      onClick={() => setStart({ ...start, member_type: tp.slug, plan_slug: tp.default_plan_slug || start.plan_slug })}
                      className={`text-left p-4 rounded-xl border transition-colors ${
                        start.member_type === tp.slug
                          ? 'border-[#D9B35A] bg-[#D9B35A]/12'
                          : 'border-white/15 hover:border-white/35'
                      }`}>
                      <span className={`block text-sm font-bold ${start.member_type === tp.slug ? 'text-[#E9CF8E]' : 'text-white/85'}`}>{tp.titles?.[lang] || tp.titles?.fr}</span>
                      <span className="block text-[11px] text-white/55 mt-1">{tp.descriptions?.[lang] || tp.descriptions?.fr}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>{t('vendorOnboarding.company')}</label>
                <input required className={inputCls} data-testid="vendor-company-input" value={start.company} onChange={(e) => setStart({ ...start, company: e.target.value })} placeholder={t('vendorOnboarding.companyPh')} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.legalForm')}</label>
                <select required className={inputCls} data-testid="vendor-legal-form-select" value={start.legal_form} style={{ colorScheme: 'dark' }}
                  onChange={(e) => setStart({ ...start, legal_form: e.target.value })}>
                  <option value="" style={{ background: '#2A1045' }}>—</option>
                  {['SARL', 'EURL', 'SAS', 'SASU', 'SA', 'SCOP', 'SCIC', 'Association', 'Entreprise individuelle', 'Micro-entreprise', 'Autre'].map((lf) => (
                    <option key={lf} value={lf} style={{ background: '#2A1045' }}>{lf}</option>
                  ))}
                </select></div>
              <div><label className={labelCls}>{t('vendorOnboarding.firstName')}</label>
                <input required className={inputCls} data-testid="vendor-firstname-input" value={start.first_name} onChange={(e) => setStart({ ...start, first_name: e.target.value })} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.lastName')}</label>
                <input required className={inputCls} data-testid="vendor-lastname-input" value={start.last_name} onChange={(e) => setStart({ ...start, last_name: e.target.value })} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.email')}</label>
                <input required type="email" className={inputCls} data-testid="vendor-email-input" value={start.email} onChange={(e) => setStart({ ...start, email: e.target.value })} placeholder={t('vendorOnboarding.emailPh')} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.phone')}</label>
                <PhoneInput dial={dial} number={phoneNum} onDialChange={setDial} onNumberChange={setPhoneNum} testId="vendor-phone-input" /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.country')}</label>
                <CountrySelect value={start.country} testId="vendor-country-select"
                  onChange={(code) => setStart({ ...start, country: code })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>{t('vendorOnboarding.siret')}</label>
                <input required minLength={9} className={inputCls} data-testid="vendor-siret-input" value={start.siret} onChange={(e) => setStart({ ...start, siret: e.target.value })} placeholder={t('vendorOnboarding.siretPh')} /></div>
            </div>
            <PlanPicker value={start.plan_slug} memberType={start.member_type} onPlansLoaded={setPlans}
              onChange={(slug) => setStart({ ...start, plan_slug: slug })} />
            {(() => {
              const plan = plans.find((p) => p.slug === start.plan_slug);
              if (!plan) return null;
              const rate = vatRateFor(start.country);
              const ht = (plan.price_cents || 0) / 100;
              const ttc = ht * (1 + rate / 100);
              const fmt = (v) => v.toFixed(2).replace('.', ',');
              return (
                <p className="text-xs text-center font-semibold text-[#E9CF8E]" data-testid="vat-total-line">
                  {rate > 0
                    ? t('vendorOnboarding.vatTotal', { ht: fmt(ht), rate: String(rate).replace('.', ','), ttc: fmt(ttc) })
                    : t('vendorOnboarding.vatTotalExempt', { ht: fmt(ht) })}
                </p>
              );
            })()}
            {sponsorCode && (
              <p className="text-xs text-center font-semibold rounded-lg py-2 px-3"
                style={{ background: 'rgba(217,179,90,0.12)', border: '1px solid rgba(217,179,90,0.35)', color: '#E9CF8E' }}
                data-testid="sponsor-code-banner">
                Code parrain {sponsorCode.toUpperCase()} détecté — il sera appliqué automatiquement à votre première connexion, aucune saisie nécessaire.
              </p>
            )}
            <label className="flex items-start gap-2.5 text-xs text-white/75 cursor-pointer rounded-lg p-3"
              style={{ background: 'rgba(217,179,90,0.06)', border: '1px solid rgba(217,179,90,0.25)' }}>
              <input type="checkbox" required checked={start.cgu_accepted}
                onChange={(e) => setStart({ ...start, cgu_accepted: e.target.checked })}
                data-testid="cgu-accept-checkbox" className="mt-0.5 accent-[#D9B35A]" />
              <span>
                J'accepte pleinement et sans réserve les{' '}
                <a href="/legal/cg-oscop" target="_blank" rel="noreferrer" className="underline text-[#E9CF8E]" data-testid="cgu-link">
                  Conditions Générales d'Utilisation de la plateforme CommunityPlace
                </a>{' '}
                (article 1.1) ainsi que la{' '}
                <a href="/legal/politique-confidentialite" target="_blank" rel="noreferrer" className="underline text-[#E9CF8E]">politique de confidentialité</a>.
              </span>
            </label>
            <button type="submit" disabled={busy} data-testid="vendor-pay-btn"
              className="w-full h-13 py-3.5 rounded-xl inline-flex items-center justify-center gap-2 text-sm font-bold disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #D9B35A 0%, #b8933e 100%)', color: '#1F0A33' }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} {t('vendorOnboarding.payBtn')}
            </button>
            <p className="text-[11px] text-white/45 text-center">{t('vendorOnboarding.payNote')}</p>
          </form>
        )}

        {step === 0 && (
          <div className="mt-10 space-y-4" data-testid="adhesion-territories-block">
            <h2 className="text-base md:text-lg font-bold text-center" style={{ color: '#F7F2E9' }}>
              {t('vendorOnboarding.territoriesTitle', 'La coopérative grandit sur chaque territoire')}
            </h2>
            <TerritoryMap zone={goalZone} onSelect={setGoalZone} />
            <TerritoryGoals />
          </div>
        )}

        {step === 1 && (
          <form onSubmit={saveConvention} className="glass-panel rounded-[22px] p-6 space-y-4" data-testid="vendor-convention-form">
            <p className="text-sm text-white/70">{t('vendorOnboarding.convIntro')}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>{t('vendorOnboarding.formeSociale')}</label>
                <input className={inputCls} value={conv.forme_sociale} onChange={(e) => setConv({ ...conv, forme_sociale: e.target.value })} placeholder={t('vendorOnboarding.formeSocialePh')} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.capital')}</label>
                <input required className={inputCls} data-testid="vendor-capital-input" value={conv.capital} onChange={(e) => setConv({ ...conv, capital: e.target.value })} placeholder="10 000" /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.rcs')}</label>
                <input required className={inputCls} data-testid="vendor-rcs-input" value={conv.rcs_ville} onChange={(e) => setConv({ ...conv, rcs_ville: e.target.value })} placeholder="Pointe-à-Pitre" /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.adresse')}</label>
                <input required className={inputCls} value={conv.adresse} onChange={(e) => setConv({ ...conv, adresse: e.target.value })} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.repPrenom')}</label>
                <input required className={inputCls} value={conv.rep_prenom} onChange={(e) => setConv({ ...conv, rep_prenom: e.target.value })} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.repNom')}</label>
                <input required className={inputCls} data-testid="vendor-rep-nom-input" value={conv.rep_nom} onChange={(e) => setConv({ ...conv, rep_nom: e.target.value })} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.repQualite')}</label>
                <input required className={inputCls} value={conv.rep_qualite} onChange={(e) => setConv({ ...conv, rep_qualite: e.target.value })} placeholder={t('vendorOnboarding.repQualitePh')} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.lieuSignature')}</label>
                <input required className={inputCls} value={conv.lieu_signature} onChange={(e) => setConv({ ...conv, lieu_signature: e.target.value })} placeholder="Baie-Mahault" /></div>
            </div>
            <div>
              <label className={labelCls}>{t('vendorOnboarding.territoires')}</label>
              <div className="flex flex-wrap gap-2">
                {TERRITOIRES.map((tt) => (
                  <button type="button" key={tt} data-testid={`vendor-territoire-${tt}`}
                    onClick={() => setConv({ ...conv, territoires: conv.territoires.includes(tt) ? conv.territoires.filter((x) => x !== tt) : [...conv.territoires, tt] })}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      conv.territoires.includes(tt) ? 'bg-[#D9B35A]/20 border-[#D9B35A] text-[#E9CF8E]' : 'border-white/20 text-white/60 hover:border-white/40'
                    }`}>{tt}</button>
                ))}
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="vendor-convention-submit"
              className="w-full py-3.5 rounded-xl inline-flex items-center justify-center gap-2 text-sm font-bold disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #D9B35A 0%, #b8933e 100%)', color: '#1F0A33' }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {t('vendorOnboarding.generateBtn')}
            </button>
          </form>
        )}

        {step === 2 && (
          <div className="glass-panel rounded-[22px] p-6 space-y-4" data-testid="vendor-sign-step">
            <p className="text-sm text-white/70 flex items-center gap-2"><FileSignature className="w-4 h-4 text-[#D9B35A]" />
              {t('vendorOnboarding.signIntro')}</p>
            <iframe title="Convention" src={`${API}/vendor-onboarding/${oid}/convention.pdf`} className="w-full rounded-xl border border-[#D9B35A]/25" style={{ height: 420, background: 'white' }} data-testid="vendor-convention-preview" />
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>{t('vendorOnboarding.signNom')}</label>
                <input required className={inputCls} data-testid="vendor-sign-nom" value={sign.nom} onChange={(e) => setSign({ ...sign, nom: e.target.value })} /></div>
              <div><label className={labelCls}>{t('vendorOnboarding.signQualite')}</label>
                <input required className={inputCls} data-testid="vendor-sign-qualite" value={sign.qualite} onChange={(e) => setSign({ ...sign, qualite: e.target.value })} placeholder={t('vendorOnboarding.signQualitePh')} /></div>
            </div>
            <label className="flex items-start gap-2.5 text-sm text-white/80 cursor-pointer">
              <input type="checkbox" checked={sign.lu_approuve} data-testid="vendor-sign-checkbox"
                onChange={(e) => setSign({ ...sign, lu_approuve: e.target.checked })} className="mt-0.5 accent-[#D4AF37]" />
              <span>{t('vendorOnboarding.luApprouve')}</span>
            </label>
            <button type="button" onClick={doSign} disabled={busy} data-testid="vendor-sign-btn"
              className="w-full py-3.5 rounded-xl inline-flex items-center justify-center gap-2 text-sm font-bold disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #D9B35A 0%, #b8933e 100%)', color: '#1F0A33' }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} {t('vendorOnboarding.signBtn')}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="glass-panel rounded-[22px] p-10 text-center" data-testid="vendor-done-step">
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#7BC94E] mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">{t('vendorOnboarding.doneTitle')}</h2>
            <p className="text-white/70 text-sm flex items-center justify-center gap-2 mb-4">
              <Mail className="w-4 h-4 text-[#D9B35A]" /> {t('vendorOnboarding.doneEmail')}
            </p>
            <p className="text-white/50 text-xs mb-6">{t('vendorOnboarding.doneHint')}</p>
            <a href={`${API}/vendor-onboarding/${oid}/convention.pdf`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
              style={{ background: 'rgba(217,179,90,0.15)', color: '#E9CF8E', border: '1px solid rgba(217,179,90,0.4)' }}
              data-testid="vendor-download-convention">
              {t('vendorOnboarding.downloadBtn')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
